import React, { useEffect, useId, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { decideApproval } from '../../_services/approval.service';
import { authenticatedIdentity } from '../../_services/apiFetch';

function releaseBusy(operation) {
    if (operation.released) return;

    operation.released = true;
    operation.onBusyChange?.(false);
}

export function ApprovalDecisionActions({
    recordType,
    recordId,
    recordLabel,
    currentLevel = null,
    canApprove = false,
    canReject = false,
    disabled = false,
    submitDecision,
    onDecision,
    onError,
    onBusyChange,
    // Optional custom look (Sales, Purchasing and Finance use their own
    // buttons and dialogs). The default is the Bootstrap look.
    renderButtons,
    renderDialog,
}) {
    const [action, setAction] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [error, setError] = useState(null);
    const inFlight = useRef(null);
    const mounted = useRef(false);
    const busyCallback = useRef(onBusyChange);
    busyCallback.current = onBusyChange;
    const identity = authenticatedIdentity();
    const targetKey = JSON.stringify([identity, recordType, String(recordId), currentLevel]);
    const currentTarget = useRef({ key: targetKey });
    if (currentTarget.current.key !== targetKey) {
        currentTarget.current = { key: targetKey };
    }

    const context = currentTarget.current;
    const dialogId = useId();

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        setAction(null);
        setReason('');
        setBusy(false);
        setCompleted(false);
        setError(null);
        return () => {
            if (inFlight.current?.context === context) {
                const operation = inFlight.current;
                inFlight.current = null;
                releaseBusy(operation);
            }
        };
    }, [context]);

    const current = () =>
        mounted.current &&
        currentTarget.current === context &&
        identity === authenticatedIdentity();
    const allowed = action === 'approve' ? canApprove : canReject;
    const blocked = busy || disabled || completed;

    const cancel = () => {
        if (busy) return;

        setAction(null);
        setReason('');
        setError(null);
    };

    // A custom dialog may keep its own reason box and pass the text in here.
    const confirm = async (enteredReason) => {
        const typedReason = typeof enteredReason === 'string' ? enteredReason : reason;
        const rejectionReason = action === 'reject' ? typedReason.trim() : null;
        if (!action || !allowed || blocked || inFlight.current?.context === context) return;
        if (action === 'reject' && !rejectionReason) return;

        const decision = { action, reason: rejectionReason };
        const operation = { context, onBusyChange: busyCallback.current, released: false };
        inFlight.current = operation;
        setBusy(true);
        setError(null);
        operation.onBusyChange?.(true);
        let saved = false;
        try {
            const result = submitDecision
                ? await submitDecision(action, rejectionReason)
                : await decideApproval(recordType, recordId, action, rejectionReason);
            if (result?.success === false) {
                throw new Error(result.error || result.message || 'Approval decision failed');
            }

            saved = true;
            window.dispatchEvent(new CustomEvent('refreshPendingApprovalCounts'));
            if (!current()) return;

            setCompleted(true);
            setAction(null);
            setReason('');
            await onDecision?.(result, decision);
        } catch (failure) {
            if (!current()) return;

            const reported = saved
                ? Object.assign(
                    new Error('Decision saved, but latest details could not be refreshed. Reload the page.'),
                    { cause: failure, status: failure.status }
                )
                : failure;
            setError(reported);
            onError?.(reported, {
                action: decision.action,
                phase: saved ? 'refresh' : 'request',
            });
        } finally {
            if (inFlight.current === operation) {
                inFlight.current = null;
                releaseBusy(operation);
            }

            if (current()) {
                setBusy(false);
            }
        }
    };

    if ((!canApprove && !canReject) || !recordType || !recordId) return null;

    const startApprove = () => {
        setError(null);
        setAction('approve');
    };
    const startReject = () => {
        setError(null);
        setReason('');
        setAction('reject');
    };

    if (renderButtons && renderDialog) {
        return <>
            {renderButtons({ canApprove, canReject, disabled: blocked, onApprove: startApprove, onReject: startReject })}
            {error && !action && <div role="alert" style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>{error.message}</div>}
            {renderDialog({ action, recordLabel: recordLabel || `request #${recordId}`, busy, error, onConfirm: confirm, onCancel: cancel })}
        </>;
    }

    return <>
        <div className="d-flex align-items-center gap-2 flex-nowrap">
            {canApprove && <Button
                variant="success"
                className="text-nowrap flex-shrink-0"
                disabled={blocked}
                onClick={startApprove}
            >Approve</Button>}
            {canReject && <Button
                variant="danger"
                className="text-nowrap flex-shrink-0"
                disabled={blocked}
                onClick={startReject}
            >Reject</Button>}
        </div>
        {error && !action && <Alert variant="warning" className="mt-2">{error.message}</Alert>}
        <Modal
            show={Boolean(action)}
            onHide={cancel}
            backdrop={busy ? 'static' : true}
            keyboard={!busy}
            centered
            aria-labelledby={dialogId}
        >
            <Modal.Header closeButton={!busy}>
                <Modal.Title id={dialogId}>{action === 'reject' ? 'Reject request' : 'Approve request'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{action === 'reject' ? 'Reject' : 'Approve'} <strong>{recordLabel || `request #${recordId}`}</strong>?</p>
                {action === 'reject' && <Form.Group>
                    <Form.Label htmlFor={`${dialogId}-reason`}>Reason for rejection (required)</Form.Label>
                    <Form.Control
                        id={`${dialogId}-reason`}
                        aria-label="Reason for rejection"
                        as="textarea"
                        rows={3}
                        autoFocus
                        disabled={busy}
                        value={reason}
                        placeholder="Reason for rejection (required)"
                        onChange={event => setReason(event.target.value)}
                    />
                </Form.Group>}
                {error && <Alert variant="danger" className="mt-2">{error.message}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" disabled={busy} onClick={cancel}>Cancel</Button>
                <Button
                    variant={action === 'reject' ? 'danger' : 'success'}
                    disabled={blocked || !allowed || (action === 'reject' && !reason.trim())}
                    onClick={() => confirm()}
                >
                    {action === 'reject' ? 'Reject' : 'Approve'}
                </Button>
            </Modal.Footer>
        </Modal>
    </>;
}
